<template>
  <div class="d-flex justify-content-md-end mt-2 align-items-center dashboard-filters">
    <div
      class="dashboard-filters__timezone mr-md-2"
      :class="{ 'dashboard-filters__timezone--compact': compact }"
    >
      <span
        v-if="compact"
        class="dashboard-filters__timezone-label"
      >
        <font-awesome-icon
          class="mr-2"
          :icon="['fas', 'globe-americas']"
        />
        <span data-cy="timezone-abbreviation">{{ timeZoneAbbreviation }}</span>
      </span>

      <BaseFormSelect
        id="timezone"
        v-model="timeZoneLocalCopy"
        internal-platform-input
        :small="true"
        name="Timezone"
        data-cy="select-timezone"
        text-field="display"
        value-field="id"
        form-group-class="mb-0"
        :options="timeZonesList"
        horizontal
        width="9"
      />
    </div>

    <div class="dashboard-filters__date-range position-relative">
      <!-- Measures the picker's text, so the compact picker is exactly as wide as what it shows -->
      <span
        v-if="compact"
        ref="dateRangeSizer"
        class="dashboard-filters__date-sizer"
        aria-hidden="true"
      >{{ dateRangeText }}</span>

      <v-datepicker
        v-model="dateRangeLocalCopy"
        :style="compact && dateRangeWidth ? { width: dateRangeWidth } : null"
        :format="dateRangeFormat"
        range-separator="  -  "
        :type="compact ? 'date' : 'datetime'"
        :popup-class="{ 'dashboard-filters__popup--compact': compact }"
        range
        confirm
        :clearable="!compact"
        :render-input-text="compact ? renderDateRangeText : undefined"
        icon-calendar=""
        input-class="mx-input custom-date-range-selector dashboard-filters__item custom-form-input--sm"
        data-cy="multiselect-daterange"
        confirm-text="Apply"
        :use12h="true"
        :shortcuts="shortcuts"
      >
        <template slot="icon-calendar">
          <font-awesome-icon
            v-if="compact"
            class="dashboard-filters__calendar-icon"
            :icon="['fas', 'calendar-alt']"
          />
          <div v-else />
        </template>
        <template slot="icon-clear">
          <div />
        </template>
      </v-datepicker>
    </div>

    <div class="dashboard-filters__refresh ml-md-2 dashboard-filters__item">
      <b-form-group>
        <b-button-group>
          <BaseButtonLoader
            size="sm"
            data-cy="button-auto-refresh"
            class="mb-0"
            button-class="dashboard-filters__item dashboad-filters__refresh-button"
            nowrap
            :width="3"
            @click="() => refresh()"
          >
            <font-awesome-icon
              class="mr-0"
              :icon="['fas', 'sync']"
            />
          </BaseButtonLoader>
        </b-button-group>
      </b-form-group>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import { nowInTimeZone, timeZoneAbbreviation } from '@/utils/dateTimeUtils'

// The compact bar sits on pages capped at 90 days, so it drops the long ranges and adds 30 / 90 days
const COMPACT_SHORTCUTS = ['Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'This Week', 'This Month', 'Last Month']

export default {
  name: 'DashboardFilters',
  props: {
    toggleAutoRefresh: {
      type: Function,
      default: () => {}
    },
    autoRefresh: {
      type: Boolean,
      default: false
    },
    refresh: {
      type: Function,
      default: () => {}
    },

    dateRange: {
      type: Array,
      default() {
        return []
      }
    },

    timeZone: {
      type: String,
      default: ''
    },

    maxDateRangeInDays: {
      type: Number,
      default: 31
    },

    // For a one-row filter bar: short time zone names, and whole days only (no times, a range runs from 00:00 on
    // the first day to 23:59 on the last)
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      timezone: null,
      dateRangeWidth: null
    }
  },
  computed: {
    ...mapState('common-entities', [
      'timeZones'
    ]),

    dateRangeFormat() {
      return this.compact ? 'MMM D, YYYY' : 'MMM D, YYYY h:mm A'
    },

    shortcuts() {
      // timeZone is a Windows id ("Eastern Standard Time"), which moment.tz does not know
      const getM = () => nowInTimeZone(this.timeZone)
      const lastDays = days => () => {
        const m = getM()
        const end = m.clone().endOf('day').toDate()
        const start = m.clone().subtract(days - 1, 'days').startOf('day').toDate()
        return [start, end]
      }
      const shortcuts = [
        {
          text: 'Today',
          onClick: () => {
            const m = getM()
            const start = m.clone().startOf('day').toDate()
            const end = m.clone().endOf('day').toDate()
            return [start, end]
          }
        },
        {
          text: 'Yesterday',
          onClick: () => {
            const m = getM().subtract(1, 'days')
            const start = m.clone().startOf('day').toDate()
            const end = m.clone().endOf('day').toDate()
            return [start, end]
          }
        },
        {
          text: 'Last 7 Days',
          onClick: () => {
            const m = getM()
            const end = m.clone().endOf('day').toDate()
            const start = m.clone().subtract(6, 'days').startOf('day').toDate()
            return [start, end]
          }
        },
        {
          text: 'This Week',
          onClick: () => {
            const m = getM()
            const start = m.clone().startOf('week').toDate()
            const end = m.clone().endOf('week').toDate()
            return [start, end]
          }
        },
        {
          text: 'Last Week',
          onClick: () => {
            const m = getM().subtract(1, 'week')
            const start = m.clone().startOf('week').toDate()
            const end = m.clone().endOf('week').toDate()
            return [start, end]
          }
        },
        {
          text: 'This Month',
          onClick: () => {
            const m = getM()
            const start = m.clone().startOf('month').toDate()
            const end = m.clone().endOf('month').toDate()
            return [start, end]
          }
        },
        {
          text: 'Last Month',
          onClick: () => {
            const m = getM().subtract(1, 'month')
            const start = m.clone().startOf('month').toDate()
            const end = m.clone().endOf('month').toDate()
            return [start, end]
          }
        },
        {
          text: 'This Year',
          onClick: () => {
            const m = getM()
            const start = m.clone().startOf('year').toDate()
            const end = m.clone().endOf('year').toDate()
            return [start, end]
          }
        },
        {
          text: 'Last 5 Years',
          onClick: () => {
            const m = getM()
            const end = m.clone().endOf('day').toDate()
            const start = m.clone().subtract(5, 'years').startOf('day').toDate()
            return [start, end]
          }
        }
      ]

      if (!this.compact) {
        return shortcuts
      }

      const available = [
        ...shortcuts,
        { text: 'Last 30 Days', onClick: lastDays(30) },
        { text: 'Last 90 Days', onClick: lastDays(90) }
      ]

      return COMPACT_SHORTCUTS.map(text => available.find(shortcut => shortcut.text === text))
    },

    dateRangeLocalCopy: {
      get() {
        return this.dateRange
      },

      set([from, to]) {
        if (this.compact && from && to) {
          // The date picker gives both days at 00:00
          const start = new Date(from)
          const end = new Date(to)
          start.setHours(0, 0, 0, 0)
          end.setHours(23, 59, 59, 999)
          this.$emit('date-range-updated', [start, end])
          return
        }

        this.$emit('date-range-updated', [from, to])
      }
    },

    timeZoneLocalCopy: {
      get() {
        return this.timeZone
      },
      set(value) {
        this.$emit('update-time-zone', value)
      }
    },

    // Short name of the range for headings: the shortcut it matches ("Last 7 Days"), otherwise its days without the
    // year ("Oct 19 - Oct 26", or "Oct 19" for one day)
    dateRangeLabel() {
      const [from, to] = this.dateRange || []

      if (!from || !to) {
        return ''
      }

      const shortcut = this.matchingShortcut([from, to])

      if (shortcut) {
        return shortcut.text
      }

      const first = moment(from).format('MMM D')
      const last = moment(to).format('MMM D')

      return first === last ? first : `${first} - ${last}`
    },

    dateRangeText() {
      return this.renderDateRangeText(this.dateRange)
    },

    autoRefreshButtonVariant() {
      return this.autoRefresh ? 'primary' : 'default'
    },
    
    timeZonesList() {
      const timeZonesList = this.compact
        ? this.timeZones.map(timeZone => ({ ...timeZone, display: this.shortTimeZoneName(timeZone.display) }))
        : [...this.timeZones]
      timeZonesList.unshift({ display: 'Please select', value: null })
      return timeZonesList
    },

    // Shown over the closed compact select, whose own text is hidden: a native select box always shows the
    // selected option's list text
    timeZoneAbbreviation() {
      return timeZoneAbbreviation(this.timeZone)
    },
  },

  watch: {
    dateRangeText() {
      this.$nextTick(this.fitDateRangeWidth)
    },

    dateRangeLabel: {
      immediate: true,
      handler(label) {
        this.$emit('date-range-label', label)
      }
    }
  },

  mounted() {
    if (this.compact) {
      this.fitDateRangeWidth()
      // The web font can arrive after the first measure and change the text's width
      document.fonts?.ready?.then(() => this.fitDateRangeWidth())
    }
  },

  methods:{
    // The text's width in the input's own font, plus the input's padding (room for the calendar icon) and borders
    fitDateRangeWidth() {
      const sizer = this.$refs.dateRangeSizer
      const input = this.$el?.querySelector?.('.custom-date-range-selector')

      if (!sizer || !input) {
        return
      }

      const style = getComputedStyle(input)
      sizer.style.font = style.font
      sizer.style.letterSpacing = style.letterSpacing

      const extras = ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth']
        .reduce((sum, key) => sum + (parseFloat(style[key]) || 0), 0)

      this.dateRangeWidth = `${Math.ceil(sizer.getBoundingClientRect().width + extras)}px`
    },

    // The compact shortcut a range covers, compared by day as the picker shows it
    matchingShortcut([from, to]) {
      if (!this.compact || !from || !to) {
        return null
      }

      const day = date => moment(date).format('YYYY-MM-DD')

      return this.shortcuts.find(({ onClick }) => {
        const [start, end] = onClick()
        return day(start) === day(from) && day(end) === day(to)
      }) || null
    },

    // The compact picker shows a shortcut's name ("Today") instead of its dates; any other range as its dates
    renderDateRangeText(range) {
      const [from, to] = Array.isArray(range) ? range : []

      if (!from || !to) {
        return ''
      }

      const shortcut = this.matchingShortcut([from, to])

      if (shortcut) {
        return shortcut.text
      }

      return [from, to].map(date => moment(date).format(this.dateRangeFormat)).join('  -  ')
    },

    // "(UTC-05:00) Eastern Time (US & Canada)" -> "UTC-05:00 Eastern Time", "(UTC+02:00) Helsinki, Kyiv, ..." ->
    // "UTC+02:00 Helsinki", "(UTC-11:00) Coordinated Universal Time-11" -> "UTC-11:00"
    shortTimeZoneName(display) {
      const match = /^\((UTC[^)]*)\)\s*(.*)$/.exec(display || '')

      if (!match) {
        return display
      }

      const [, offset, name] = match

      if (name.startsWith('Coordinated Universal Time')) {
        return offset
      }

      return `${offset} ${name.replace(/\s*\([^)]*\)\s*$/, '').split(',')[0].trim()}`
    },

    daysToMilliseconds(days) {
      return days * 24 * 60 * 60 * 1000
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard-filters {
  margin: 0 10px;
}

// Off screen, but laid out, so it can be measured; spaces kept as the input shows them
.dashboard-filters__date-sizer {
  position: absolute;
  top: 0;
  left: 0;
  visibility: hidden;
  white-space: pre;
  pointer-events: none;
}

// The select keeps working underneath; only its text is hidden behind the abbreviation
.dashboard-filters__timezone--compact {
  position: relative;

  ::v-deep select#timezone {
    color: transparent;

    option {
      color: var(--component-custom-input-font-color);
    }
  }
}

.dashboard-filters__timezone-label {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  z-index: 1;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 0.875rem;
  color: var(--component-custom-input-font-color);
}

/*
 * Where the sidebar is a drawer the row is in from each side by the panels' margin ($panel-margin), so
 * its edges are theirs rather than 14px further out on each side. Below md it is as wide as they are
 * (below). On a phone on its side and a tablet upright it stays one row on the right, and its right
 * edge is theirs (docs/softphone-mobile-plan.md, P3.11).
 */
@include sidebar-drawer {
  .dashboard-filters {
    margin: 0 $panel-margin;
  }
}

/*
 * A phone has no room for the three side by side (the date range alone is 331px), and the row is
 * aligned right, so it ran off the left of the screen where nothing could scroll to it. Below md it
 * wraps instead: the time zone and refresh on the first line, the date range under them at the
 * screen's width, which is the panels' width (above). The row's alignment to the right and the
 * margins beside the date range are md-up utilities in the template, and the gap spaces it here.
 */
@include media-breakpoint-down(sm) {
  .dashboard-filters {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .dashboard-filters__timezone {
    flex: 1 1 0;
    min-width: 0;

    // The select has no label, but its form group keeps an empty label column (above it below sm,
    // beside it from sm). Without it the select takes the line and is level with the refresh button.
    ::v-deep .col-form-label {
      display: none;
    }

    ::v-deep .col-form-label + div {
      flex: 1 1 100%;
      max-width: 100%;
    }
  }

  .dashboard-filters__date-range {
    order: 1;
    flex: 1 1 100%;

    ::v-deep .mx-datepicker-range {
      width: 100%;
    }

    // On the narrowest phones the range is a size smaller, so that all of it shows (see below).
    @include media-breakpoint-down(xs) {
      ::v-deep .mx-input {
        font-size: 13px;
      }
    }

    // At 360 the box is 312px, 1.5rem in from each side like the panels, and the range at 13px needs
    // about 321: a size smaller again, below 375px only.
    @include narrow-phone {
      ::v-deep .mx-input {
        font-size: 12px;
      }
    }
  }
}

// 44px targets wherever a finger is the pointer, a phone on its side included.
@include touch-screen {
  .dashboard-filters__timezone ::v-deep select,
  .dashboard-filters__date-range ::v-deep .mx-input,
  .dashboard-filters__refresh ::v-deep .btn {
    @include touch-target;
  }

  // The range is about 315px of text in a 331px box whose padding makes room for the picker's icons,
  // which this filter leaves empty. With the padding it is cut off at "11:5", and a range that cannot
  // be typed in cannot be scrolled either. A desktop is left as it was.
  .dashboard-filters__date-range ::v-deep .mx-input {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .dashboard-filters__refresh {
    height: $touch-target-size;
  }
}

// From md up the box keeps its own width, and the last few pixels of the range come from the time
// zone beside it, which shrinks.
@include media-breakpoint-up(md) {
  @include coarse-pointer {
    .dashboard-filters__date-range ::v-deep .mx-datepicker-range {
      width: 345px;
    }
  }
}

::v-deep .daterangepicker {
  .ranges {
    overflow-x: hidden;
    padding: 8px;
    min-width: 140px; /* Increased width to prevent scroll */
    
    li {
      margin: 2px 4px;
      padding: 8px 12px;
    }
  }

  .drp-buttons {
    padding: 12px;
  }
}
</style>

<style lang="scss">
// The popup is appended to the body, out of reach of the scoped styles. The default 100px shortcut column is too
// narrow for "Last 30 Days" and scrolls sideways.
.dashboard-filters__popup--compact {
  .mx-datepicker-sidebar {
    width: 120px;
    overflow: hidden;
  }

  .mx-datepicker-sidebar + .mx-datepicker-content {
    margin-left: 120px;
  }
}
</style>
